/**
 * Feature slug mappings for different locales
 * Maps locale-specific feature slugs to internal identifiers
 */
export const featureSlugMap: Record<string, Record<string, string>> = {
  en: {
    'bitcoin-integration': 'bitcoin-integration',
    'sms-integration': 'sms-integration',
    'e-pin': 'e-pin',
    'e-wallet': 'e-wallet',
    'e-mail-system': 'e-mail-system',
    'ticket-system': 'ticket-system',
    'e-voucher': 'e-voucher',
    'replicating-website': 'replicating-website',
    'genealogy-tree': 'genealogy-tree',
    'marketing-automation': 'marketing-automation',
    'payment-gateway-integration': 'payment-gateway-integration',
    'integration-with-e-commerce-solutions': 'integration-with-e-commerce-solutions',
    'crypto-currency-mlm-software': 'crypto-currency-mlm-software',
  },
  es: {
    'integracion-bitcoin': 'bitcoin-integration',
    'integracion-sms': 'sms-integration',
    'pin-electronico': 'e-pin',
    'billetera-electronica': 'e-wallet',
    'sistema-de-correo-electronico': 'e-mail-system',
    'sistema-de-tickets': 'ticket-system',
    'e-voucher': 'e-voucher',
    'cupon-electronico': 'e-voucher',
    'sitio-web-replicador': 'replicating-website',
    'arbol-genealogico': 'genealogy-tree',
    'automatizacion-de-marketing': 'marketing-automation',
    'integracion-de-pasarela-de-pago': 'payment-gateway-integration',
    'integracion-con-soluciones-de-comercio-electronico': 'integration-with-e-commerce-solutions',
    'software-mlm-de-criptomonedas': 'crypto-currency-mlm-software',
  },
  fr: {
    'integration-bitcoin': 'bitcoin-integration',
    'integration-sms': 'sms-integration',
    'pin-electronique': 'e-pin',
    'portefeuille-electronique': 'e-wallet',
    'systeme-de-courrier-electronique': 'e-mail-system',
    'systeme-de-tickets': 'ticket-system',
    'e-voucher': 'e-voucher',
    'bon-electronique': 'e-voucher',
    'site-web-repliquant': 'replicating-website',
    'arbre-genealogique': 'genealogy-tree',
    'automatisation-marketing': 'marketing-automation',
    'integration-passerelle-paiement': 'payment-gateway-integration',
    'integration-solutions-e-commerce': 'integration-with-e-commerce-solutions',
    'logiciel-mlm-cryptomonnaie': 'crypto-currency-mlm-software',
    // Common incorrect/alternative slugs
    'frbitcoin': 'bitcoin-integration',
    'bitcoin': 'bitcoin-integration',
    'integracion-bitcoin': 'bitcoin-integration', // Spanish slug used incorrectly
  },
  it: {
    'integrazione-bitcoin': 'bitcoin-integration',
    'integrazione-sms': 'sms-integration',
    'pin-elettronico': 'e-pin',
    'portafoglio-elettronico': 'e-wallet',
    'sistema-di-posta-elettronica': 'e-mail-system',
    'sistema-di-biglietti': 'ticket-system',
    'e-voucher': 'e-voucher',
    'buono-elettronico': 'e-voucher',
    'sito-web-replicante': 'replicating-website',
    'albero-genealogico': 'genealogy-tree',
    'automazione-marketing': 'marketing-automation',
    'integrazione-gateway-pagamento': 'payment-gateway-integration',
    'integrazione-soluzioni-e-commerce': 'integration-with-e-commerce-solutions',
    'software-mlm-cryptovaluta': 'crypto-currency-mlm-software',
  },
  pt: {
    'integracao-bitcoin': 'bitcoin-integration',
    'integracao-sms': 'sms-integration',
    'pin-eletronico': 'e-pin',
    'carteira-eletronica': 'e-wallet',
    'sistema-de-email': 'e-mail-system',
    'sistema-de-tickets': 'ticket-system',
    'e-voucher': 'e-voucher',
    'cupom-eletronico': 'e-voucher',
    'site-web-replicador': 'replicating-website',
    'arvore-genealogica': 'genealogy-tree',
    'automacao-de-marketing': 'marketing-automation',
    'integracao-gateway-pagamento': 'payment-gateway-integration',
    'integracao-com-solucoes-de-e-commerce': 'integration-with-e-commerce-solutions',
    'software-mlm-criptomoeda': 'crypto-currency-mlm-software',
  },
  de: {
    'bitcoin-integration': 'bitcoin-integration',
    'sms-integration': 'sms-integration',
    'e-pin': 'e-pin',
    'e-wallet': 'e-wallet',
    'e-mail-system': 'e-mail-system',
    'ticket-system': 'ticket-system',
    'e-voucher': 'e-voucher',
    'egutschein': 'e-voucher',
    'replizierende-website': 'replicating-website',
    'stammbaum': 'genealogy-tree',
    'marketing-automatisierung': 'marketing-automation',
    'zahlungsgateway-integration': 'payment-gateway-integration',
    'integration-mit-e-commerce-loesungen': 'integration-with-e-commerce-solutions',
    'kryptowaehrung-mlm-software': 'crypto-currency-mlm-software',
  },
};

/**
 * Reverse mapping: feature identifier to locale-specific slugs
 */
export const featureToSlugMap: Record<string, Record<string, string>> = {
  'bitcoin-integration': {
    en: 'bitcoin-integration',
    es: 'integracion-bitcoin',
    fr: 'integration-bitcoin',
    it: 'integrazione-bitcoin',
    pt: 'integracao-bitcoin',
    de: 'bitcoin-integration',
  },
  'sms-integration': {
    en: 'sms-integration',
    es: 'integracion-sms',
    fr: 'integration-sms',
    it: 'integrazione-sms',
    pt: 'integracao-sms',
    de: 'sms-integration',
  },
  'e-pin': {
    en: 'e-pin',
    es: 'pin-electronico',
    fr: 'pin-electronique',
    it: 'pin-elettronico',
    pt: 'pin-eletronico',
    de: 'e-pin',
  },
  'e-wallet': {
    en: 'e-wallet',
    es: 'billetera-electronica',
    fr: 'portefeuille-electronique',
    it: 'portafoglio-elettronico',
    pt: 'carteira-eletronica',
    de: 'e-wallet',
  },
  'e-mail-system': {
    en: 'e-mail-system',
    es: 'sistema-de-correo-electronico',
    fr: 'systeme-de-courrier-electronique',
    it: 'sistema-di-posta-elettronica',
    pt: 'sistema-de-email',
    de: 'e-mail-system',
  },
  'ticket-system': {
    en: 'ticket-system',
    es: 'sistema-de-tickets',
    fr: 'systeme-de-tickets',
    it: 'sistema-di-biglietti',
    pt: 'sistema-de-tickets',
    de: 'ticketsystem',
  },
  'e-voucher': {
    en: 'e-voucher',
    es: 'cupon-electronico',
    fr: 'bon-electronique',
    it: 'buono-elettronico',
    pt: 'cupom-eletronico',
    de: 'egutschein',
  },
  'replicating-website': {
    en: 'replicating-website',
    es: 'sitio-web-replicador',
    fr: 'site-web-repliquant',
    it: 'sito-web-replicante',
    pt: 'site-web-replicador',
    de: 'replizierende-website',
  },
  'genealogy-tree': {
    en: 'genealogy-tree',
    es: 'arbol-genealogico',
    fr: 'arbre-genealogique',
    it: 'albero-genealogico',
    pt: 'arvore-genealogica',
    de: 'stammbaum',
  },
  'marketing-automation': {
    en: 'marketing-automation',
    es: 'automatizacion-de-marketing',
    fr: 'automatisation-marketing',
    it: 'automazione-marketing',
    pt: 'automacao-de-marketing',
    de: 'marketing-automatisierung',
  },
  'payment-gateway-integration': {
    en: 'payment-gateway-integration',
    es: 'integracion-de-pasarela-de-pago',
    fr: 'integration-passerelle-paiement',
    it: 'integrazione-gateway-pagamento',
    pt: 'integracao-gateway-pagamento',
    de: 'zahlungsgateway-integration',
  },
  'integration-with-e-commerce-solutions': {
    en: 'integration-with-e-commerce-solutions',
    es: 'integracion-con-soluciones-de-comercio-electronico',
    fr: 'integration-solutions-e-commerce',
    it: 'integrazione-soluzioni-e-commerce',
    pt: 'integracao-com-solucoes-de-e-commerce',
    de: 'integration-mit-e-commerce-loesungen',
  },
  'crypto-currency-mlm-software': {
    en: 'crypto-currency-mlm-software',
    es: 'software-mlm-de-criptomonedas',
    fr: 'logiciel-mlm-cryptomonnaie',
    it: 'software-mlm-cryptovaluta',
    pt: 'software-mlm-criptomoeda',
    de: 'kryptowaehrung-mlm-software',
  },
};

/**
 * Get feature identifier from locale-specific slug
 */
export function getFeatureFromSlug(slug: string, locale: string): string | null {
  return featureSlugMap[locale]?.[slug] || null;
}

/**
 * Get locale-specific slug from feature identifier
 * Handles both feature identifiers (e.g., 'bitcoin-integration') and existing slugs
 */
export function getSlugFromFeature(feature: string, locale: string): string | null {
  // First, try direct mapping from feature identifier
  if (featureToSlugMap[feature]?.[locale]) {
    return featureToSlugMap[feature][locale];
  }
  
  // If not found, try reverse lookup - check if the feature string is already a slug in any locale
  // and find the corresponding slug for the requested locale
  for (const [featureId, localeSlugs] of Object.entries(featureToSlugMap)) {
    // Check if the feature string matches any slug in any locale
    for (const [loc, slug] of Object.entries(localeSlugs)) {
      if (slug === feature && localeSlugs[locale]) {
        return localeSlugs[locale];
      }
    }
  }
  
  return null;
}

/**
 * Get all feature slugs for static generation
 */
export function getAllFeatureSlugs(): Array<{ slug: string; locale: string }> {
  const allSlugs: Array<{ slug: string; locale: string }> = [];
  for (const [locale, slugs] of Object.entries(featureSlugMap)) {
    for (const slug of Object.keys(slugs)) {
      allSlugs.push({ slug, locale });
    }
  }
  return allSlugs;
}

/**
 * Parse feature detail URL to extract page path and feature slug
 * Handles URLs like: "features/bitcoin-integration" or "caracteristicas-de-software-mlm/integracion-bitcoin"
 */
export function parseFeatureDetailUrl(path: string, locale: string): { pagePath: string; featureSlug: string; featureId: string | null } | null {
  // Import here to avoid circular dependencies
  const { getPageFromSlug } = require('@/lib/page-slugs');
  
  // Split the path into segments
  const segments = path.split('/').filter(Boolean);
  
  if (segments.length < 2) {
    return null;
  }
  
  // First segment should be the page path (features or its translation)
  const pagePath = segments[0];
  
  // Try to get the page ID with current locale first
  let pageId = getPageFromSlug(pagePath, locale);
  
  // If not found, try all locales to find a match
  if (!pageId) {
    const { locales } = require('@/i18n');
    for (const loc of locales) {
      pageId = getPageFromSlug(pagePath, loc);
      if (pageId === 'features' || pageId === 'mlm-software-features') {
        break;
      }
    }
  }
  
  // Check if it's the features page (both 'features' and 'mlm-software-features' are valid)
  if (pageId !== 'features' && pageId !== 'mlm-software-features') {
    return null;
  }
  
  // Second segment should be the feature slug
  const featureSlug = segments[1];
  
  // Try to get the feature ID with current locale first
  let featureId = getFeatureFromSlug(featureSlug, locale);
  
  // If not found, try all locales to find a match
  if (!featureId) {
    const { locales } = require('@/i18n');
    for (const loc of locales) {
      featureId = getFeatureFromSlug(featureSlug, loc);
      if (featureId) {
        break;
      }
    }
  }
  
  if (!featureId) {
    return null;
  }
  
  return { pagePath, featureSlug, featureId };
}

/**
 * Get feature page identifier from locale-specific slug
 * Handles both single slug and full path formats
 */
export function getFeaturePageIdentifierFromSlug(slug: string, locale: string): string | null {
  // Try parsing as full path first (page-path/feature-slug)
  const parsed = parseFeatureDetailUrl(slug, locale);
  if (parsed && parsed.featureId) {
    return parsed.featureId;
  }
  
  // Fall back to single slug format
  return getFeatureFromSlug(slug, locale);
}
